import { DEFAULT_SCOPES, type Scope } from '@mesa/core/auth/types';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import mesaLogo from '@/assets/mesa_logo_light_circle.webp';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import { sessionQueryOptions } from '@/lib/auth-query-options';
import { approveOAuthConsent, loadOAuthConsentPage, rejectOAuthConsent } from '@/server/functions/oauth';

/**
 * `/oauth/consent` — OAuth consent UI, linked from the provider's
 * `/oauth2/authorize` redirect. The search string is HMAC-signed, so it must
 * be forwarded verbatim (raw `location.searchStr`); re-serializing it breaks
 * the signature.
 */
export const Route = createFileRoute('/oauth/consent')({
  ssr: 'data-only',
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const session = await queryClient.ensureQueryData(sessionQueryOptions);
    if (!session) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: `/oauth/consent${location.searchStr}` },
      });
    }
    return {};
  },
  loader: async ({ location }) => {
    const data = await loadOAuthConsentPage({ data: { search: location.searchStr } });
    return { ...data, oauthQuery: location.searchStr };
  },
  component: ConsentPage,
});

function ConsentPage() {
  const { clientName, requestedScopes, scopeString, memberships, activeOrgId, redirectOrigin, oauthQuery } =
    Route.useLoaderData();

  const defaultOrgId = memberships.find((m) => m.id === activeOrgId)?.id ?? memberships[0]?.id ?? '';
  const [orgId, setOrgId] = useState<string>(defaultOrgId);
  // Default to the requested read/write scopes; admin is opt-in (same as the API-key screen).
  const [selectedScopes, setSelectedScopes] = useState<Scope[]>(
    requestedScopes.filter((scope) => DEFAULT_SCOPES.includes(scope))
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = selectedScopes.includes('admin');

  // read ⊂ write ⊂ admin is a strict hierarchy at the gate (`hasScope`), so admin
  // can't be combined: selecting it collapses the set to ['admin'] and locks the
  // others; deselecting falls back to the requested read/write. Mirrors tokens.tsx.
  const toggleScope = (scope: Scope) => {
    if (scope === 'admin') {
      setSelectedScopes((prev) =>
        prev.includes('admin') ? requestedScopes.filter((s) => DEFAULT_SCOPES.includes(s)) : ['admin']
      );
    } else if (!isAdmin) {
      setSelectedScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
    }
  };

  // offline_access rides along as a refresh-token lifetime flag, not a permission.
  const hiddenScopes = scopeString.split(/\s+/).filter((scope) => scope === 'offline_access');
  const approvalScope = [...selectedScopes, ...hiddenScopes].join(' ');
  const canApprove = !!orgId && (requestedScopes.length === 0 || selectedScopes.length > 0);

  const followRedirect = (url: string) => {
    // Plugin returns { redirect, url } as a 200 JSON envelope, not a 302 — navigate manually.
    window.location.assign(url);
  };

  const handleApprove = async () => {
    if (!orgId) {
      setError('Pick an organization to continue.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Select the org before approving: the token is scoped to the active org.
      const activated = await authClient.organization.setActive({ organizationId: orgId });
      if (activated.error) {
        throw new Error(activated.error.message ?? 'Could not select organization');
      }
      const result = await approveOAuthConsent({
        data: { scope: approvalScope, oauthQuery },
      });
      followRedirect(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await rejectOAuthConsent({ data: { scope: scopeString, oauthQuery } });
      followRedirect(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
      setSubmitting(false);
    }
  };

  if (memberships.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-xl font-semibold mb-2">No organizations</h1>
            <p className="text-mesa-muted mb-6">
              You must belong to at least one organization to authorize a third-party application.
            </p>
            <Button onClick={handleReject} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="h-2 bg-mesa-green" />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8">
          <div className="text-center mb-6">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-xl font-semibold mb-2">Authorize {clientName}</h1>
            <p className="text-mesa-muted text-sm">
              {clientName} wants to access your Mesa data
              {redirectOrigin ? (
                <>
                  {' '}
                  on behalf of <strong>{redirectOrigin}</strong>
                </>
              ) : null}
              .
            </p>
          </div>

          <div className="border border-mesa-border bg-mesa-cream-dark p-4 mb-6 space-y-3">
            <div>
              <div className="text-xs text-mesa-muted mb-1">Permissions</div>
              {requestedScopes.length === 0 ? (
                <div className="text-sm text-mesa-muted">None</div>
              ) : (
                <div className="space-y-2">
                  {requestedScopes.map((scope) => {
                    const checked = scope === 'admin' ? isAdmin : isAdmin || selectedScopes.includes(scope);
                    const disabled = submitting || (scope !== 'admin' && isAdmin);
                    return (
                      <label key={scope} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => toggleScope(scope)} />
                        <span className="capitalize">{scope}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="orgId" className="text-xs text-mesa-muted mb-1 block">
                Authorize for organization
              </Label>
              {memberships.length === 1 ? (
                <div className="text-sm font-medium">{memberships[0].name}</div>
              ) : (
                <select
                  id="orgId"
                  name="orgId"
                  value={orgId}
                  onChange={(event) => setOrgId(event.target.value)}
                  className="w-full border border-mesa-border bg-mesa-card text-sm px-2 py-1.5 focus:border-mesa-green focus:ring-2 focus:ring-mesa-green/20 focus:outline-none"
                  disabled={submitting}
                >
                  {memberships.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.role})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {error && (
            <div className="border border-mesa-red/30 bg-mesa-red/10 p-3 text-sm text-mesa-red mb-4">{error}</div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleReject} disabled={submitting}>
              Deny
            </Button>
            <Button className="flex-1" onClick={handleApprove} disabled={submitting || !canApprove}>
              {submitting ? 'Authorizing...' : 'Approve'}
            </Button>
          </div>
        </div>
      </div>
      <div className="h-2 bg-mesa-green" />
    </div>
  );
}
